import { useEffect } from 'react';

import { ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { useOnboardingContext } from '../../../context/OnboardingContext';

import { DetailLevelSelector } from '../preview/variants/DetailLevelSelector';
import { useLearnBasicsTaskActions } from '../useLearnBasicsTaskActions';
import { tourStepsMessages } from './tour-steps.messages';
import { TourStepShell } from './TourStepShell';

interface TourDetailStepProps {
  onContinue: () => void;
  onSkip: () => void;
}

/**
 * Guided-tour screen 3: the detail-level task from `LearnBasicsSteppedStep`'s
 * first accordion row, promoted to its own screen with the live note preview.
 */
export function TourDetailStep({ onContinue, onSkip }: TourDetailStepProps) {
  const { micTest } = useOnboardingContext();
  const { selectDetailLevel, isNoteGenerating } = useLearnBasicsTaskActions();

  // The note itself is what changes here, so make sure the preview is on it —
  // the user may arrive with a document or evidence tab open (Back, or a
  // re-traversal after the later screens).
  const { setNoteTab } = micTest;
  useEffect(() => {
    setNoteTab('note');
  }, [setNoteTab]);

  return (
    <TourStepShell
      screenName={ScreenName.OnboardingTourDetail}
      title={tourStepsMessages.detailTitle}
      subtitle={tourStepsMessages.detailSubtitle}
      onContinue={onContinue}
      onSkip={onSkip}
      // This screen lands while the preview is still writing the note (the
      // transcription screen's Continue starts generation). Moving the slider or
      // advancing mid-write acts on a note that isn't there yet.
      continueDisabled={isNoteGenerating}
      continueHint={tourStepsMessages.noteGeneratingHint}
    >
      <DetailLevelSelector
        level={micTest.detailLevel}
        onSelect={selectDetailLevel}
        disabled={isNoteGenerating}
      />
    </TourStepShell>
  );
}
